// frontend/src/services/authService.js
// 認證服務

import axios from 'axios';
import tokenManager from './tokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 建立 axios 實例
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // 重要：攜帶 Cookie（HttpOnly Refresh Token）
});

// 輔助函數：從 Cookie 讀取值
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// 請求攔截器：自動加入 JWT Token 和 CSRF Token
api.interceptors.request.use(
  (config) => {
    // 加入 JWT Token
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 加入 CSRF Token（對於狀態變更請求）
    const csrfMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
    if (csrfMethods.includes(config.method?.toUpperCase())) {
      const csrfToken = getCookie('XSRF-TOKEN');
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 回應攔截器：處理 401 錯誤並自動刷新 Token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 如果是 401 錯誤且不是刷新請求本身，且不是登入請求
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/refresh') &&
      !originalRequest.url.includes('/auth/login') // 登入請求不應觸發刷新
    ) {
      originalRequest._retry = true;

      // Refresh Token 現在在 HttpOnly Cookie 中，由瀏覽器自動管理
      // 只要可能有 Token 就嘗試刷新
      if (tokenManager.hasRefreshToken()) {
        try {
          // 嘗試刷新 Token
          const newToken = await tokenManager.refreshAccessToken();

          // 使用新 Token 重試原請求
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // 刷新失敗，清除 Token 並導向登入頁
          tokenManager.clearTokens();
          localStorage.removeItem('user');

          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }

          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

const authService = {
  /**
   * 註冊新使用者
   * @param {Object} userData - 使用者資料
   * @param {String} userData.username - 使用者名稱
   * @param {String} userData.email - 電子郵件
   * @param {String} userData.password - 密碼
   * @param {String} userData.studentId - 學號
   * @param {String} userData.className - 班級/講堂
   * @param {String} userData.realName - 真實姓名
   * @param {String} userData.applicationReason - 申請原因
   * @returns {Promise} API 回應
   */
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 登入
   * @param {String} username - 使用者名稱
   * @param {String} password - 密碼
   * @returns {Promise} API 回應
   */
  login: async (username, password) => {
    try {
      const response = await api.post('/auth/login', {
        username,
        password,
      });

      // 如果登入成功，使用 tokenManager 處理 Token
      // 注意：Refresh Token 現在由後端透過 HttpOnly Cookie 設定
      if (response.data.success && response.data.data) {
        const { accessToken, expiresIn } = response.data.data;
        // Refresh Token 不再從 response 取得，已由 Cookie 管理
        tokenManager.handleLogin(accessToken, expiresIn);

        // 儲存使用者資訊
        if (response.data.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.data.user));
        }
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 登出
   * @returns {Promise} API 回應
   */
  logout: async () => {
    try {
      await tokenManager.handleLogout();

      // 清除使用者資訊
      localStorage.removeItem('user');

      return { success: true };
    } catch (error) {
      // 即使 API 失敗也繼續登出流程
      console.error('登出錯誤:', error);
      tokenManager.clearTokens();
      localStorage.removeItem('user');
      return { success: true };
    }
  },

  /**
   * 修改密碼
   * @param {String} oldPassword - 舊密碼
   * @param {String} newPassword - 新密碼
   * @returns {Promise} API 回應
   */
  changePassword: async (oldPassword, newPassword) => {
    try {
      const response = await api.post('/auth/change-password', {
        oldPassword,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 取得目前使用者資訊
   * @returns {Promise} API 回應
   */
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 設定認證 Header
   * @param {String} token - JWT Token
   */
  setAuthHeader: (token) => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  },

  /**
   * 清除認證 Header
   */
  clearAuthHeader: () => {
    delete api.defaults.headers.common['Authorization'];
  },

  /**
   * 忘記密碼 - 請求密碼重設郵件
   * @param {String} email - 電子郵件
   * @returns {Promise} API 回應
   */
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 重設密碼
   * @param {String} token - 重設 Token
   * @param {String} newPassword - 新密碼
   * @returns {Promise} API 回應
   */
  resetPassword: async (token, newPassword) => {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 驗證 Email
   * @param {String} token - 驗證 Token
   * @returns {Promise} API 回應
   */
  verifyEmail: async (token) => {
    try {
      const response = await api.post('/auth/verify-email', { token });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 重新發送驗證信
   * @returns {Promise} API 回應
   */
  resendVerification: async () => {
    try {
      const response = await api.post('/auth/resend-verification');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default authService;
export { api };
